import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
	BasicLogin,
	GetOrganisationOfUser,
	sendPasswordResetLink,
} from 'firebaseApis/authentication';
import Toast, { ToastTypes } from 'components/ToastNotify';
import Icon from 'storybook-mui/build/components/Icon';
import { IconTypes } from 'storybook-mui/build/components/IconTypes';
import InputTextField from 'storybook-mui/build/components/TextField';
import Button from 'storybook-mui/build/components/Button';

import {
	SetAuthenticationType,
	SetUserInfo,
} from 'reducers/Authentication/AuthActions';
import { showLoading, hideLoading } from 'reducers/Alerts/AlertActions';

import { signinSchema } from './validations/signupValidation';
import SocialLogins from './SocialLogins';

export const Loginform = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const [forgotPasswordClicked, setForgotPasswordClicked] = useState(false);
	const [passwordLinkSent, setPasswordLinkSent] = useState<any>(false);
	const [passwordVisibility, setPasswordVisibility] = useState(true);

	const redirectToCreateOrChooseOrgPage = async (userId) => {
		try {
			dispatch(showLoading());
			const resultData = await GetOrganisationOfUser(userId);
			dispatch(hideLoading());
			if (resultData.length === 0) {
				history.push('/createorg');
			} else {
				history.push('/chooseorg');
			}
		} catch (err) {
			dispatch(hideLoading());
			history.push('/');
		}
	};

	const loginUser = async (values: any) => {
		try {
			dispatch(showLoading());
			const result = await BasicLogin(values);
			if (result.success) {
				Toast({ title: 'Login Successfull', type: ToastTypes.SUCCESS });
				dispatch(hideLoading());
				const dataForLocalStorage: any = {
					fName: result.data.FirstName,
					lName: result.data.LastName,
					email: result.data.Email,
					userId: result.data.User_Id,
					emailVerified: result.data.emailVerified,
					mobileType: result.data.select_Phone_Type,
					countryCode: result.data.phone_countryCode,
					phoneNumber: result.data.phoneNumber,
					landline_ext: result.data.landline_ext,
					email_optIn: result.data.email_optIn,
					sms_optIn: result.data.sms_optIn,
					voice_optIn: result.data.voice_optIn,
				};
				if (result.data.emailVerified) {
					dispatch(SetUserInfo(dataForLocalStorage));
					localStorage.setItem('user', JSON.stringify(dataForLocalStorage));
					redirectToCreateOrChooseOrgPage(result.data.User_Id);
				} else {
					dispatch(
						SetUserInfo({
							...dataForLocalStorage,
							currentUser: result.data.currentUser,
						})
					);
					history.push('/verifyemail');
				}
				dispatch(SetAuthenticationType('LOGIN'));
			} else {
				dispatch(hideLoading());
				Toast({ title: result.error, type: ToastTypes.ERROR });
			}
		} catch (error) {
			dispatch(hideLoading());
			Toast({ title: 'Something went wrong', type: ToastTypes.ERROR });
		}
	};

	const loginForm = useFormik({
		validationSchema: signinSchema,
		validateOnChange: false,
		initialValues: {
			Email: '',
			Password: '',
		},
		onSubmit: async (values) => {
			loginUser(values);
		},
	});

	const sendForgotPasswordLink = () => {
		sendPasswordResetLink(loginForm.values.Email)
			.then((result) => {
				if (result.success) {
					setPasswordLinkSent(true);
					Toast({ title: 'Password Reset Link Sent', type: ToastTypes.SUCCESS });
				} else {
					Toast({ title: 'Email does not exists', type: ToastTypes.ERROR });
				}
			})
			.catch(() => {
				Toast({ title: 'Something went wrong', type: ToastTypes.ERROR });
				setPasswordLinkSent(false);
			});
	};

	return (
		<>
			<SocialLogins />
			<Divider sx={{ my: 4 }}>OR</Divider>
			{!forgotPasswordClicked ? (
				<>
					<form
						className='flex mb-3 gap-4 flex-col lg:flex-row'
						onSubmit={loginForm.handleSubmit}
					>
						<div className='flex flex-col w-full gap-4'>
							<InputTextField
								name='Email'
								fieldId='email'
								autoComplete='username'
								labelName='Login email id'
								value={loginForm.values.Email}
								valid={loginForm.errors.Email === undefined}
								errorMessage='Required'
								onChange={loginForm.handleChange}
							/>
							<InputTextField
								name='Password'
								autoComplete='new-password'
								type={passwordVisibility ? 'password' : 'text'}
								fieldId='password'
								labelName='Password'
								value={loginForm.values.Password}
								errorMessage='Required'
								valid={loginForm.errors.Password === undefined}
								onChange={loginForm.handleChange}
								adornmentMessage={
									<IconButton
										aria-label='toggle password visibility'
										onClick={() => setPasswordVisibility(!passwordVisibility)}
										onMouseDown={(event) => event.preventDefault()}
									>
										{passwordVisibility ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								}
							/>
						</div>

						<Button
							title='next'
							type='submit'
							endIcon={<Icon icon={IconTypes.ArrowForwardIos} />}
						/>
					</form>
					<div className='flex flex-col lg:flex-row lg:items-center justify-between'>
						<button
							type='button'
							className='hover:underline cursor-pointer max-w-max text-sm text-gray-500 focus-within:outline-none focus-within:underline'
							onClick={() => setForgotPasswordClicked(true)}
						>
							Forgot Password ?
						</button>
						<div className='flex items-center'>
							<h1 className='text-sm text-gray-500'>Don't have an account ?</h1>
							<Button
								title={<p className='capitalize font-bold'>Sign up</p>}
								onClick={() => history.push('/signup')}
								variant='text'
								size='small'
							/>
						</div>
					</div>
				</>
			) : (
				<div>
					{passwordLinkSent ? (
						<div className='w-full'>
							<h1
								className='hover:underline uppercase hover:text-gray-800 text-gray-700 cursor-pointer text-lg text-center'
								onClick={() => {
									setForgotPasswordClicked(false);
									setPasswordLinkSent(false);
								}}
							>
								Click Here To Login
							</h1>
							<h1 className='my-5 w-full lg:w-3/4 lg:mx-auto text-base text-green-400 text-center'>
								Password reset link sent to your email,
								<br /> Please check your mail and click on the verification link to
								reset your password
							</h1>
						</div>
					) : (
						<div className='w-full lg:w-1/2 mx-auto'>
							<h1 className='my-5 text-2xl text-center text-gray-600'>
								Enter your email address to send password reset mail{' '}
							</h1>
							<InputTextField
								name='Email'
								fieldId='email'
								autoComplete='username'
								labelName='Login email id'
								value={loginForm.values.Email}
								valid={loginForm.errors.Email === undefined}
								errorMessage='Required'
								onChange={loginForm.handleChange}
							/>
							<div className='mt-5 text-center'>
								<Button
									onClick={() => sendForgotPasswordLink()}
									title='send'
									endIcon={<Icon icon={IconTypes.ArrowForwardIos} />}
									disabled={!loginForm.values.Email}
								/>
							</div>
						</div>
					)}
				</div>
			)}
		</>
	);
};

export default Loginform;

export interface ILoginForm {
	setSelectedIndex: any;
}
